import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, X } from "lucide-react";
import AdminHeader from "../../components/layout/AdminHeader";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import { toast } from "sonner";

const STATUSES = ["draft", "active", "coming_soon", "reserved", "under_contract", "sold", "unavailable", "archived"];

export default function OpportunityEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const settings = useSettings();
  const markets = settings?.get("market") || [];
  const propertyTypes = settings?.get("property_type") || [];
  const categories = settings?.get("investment_category") || [];

  const [o, setO] = useState({
    title: "", country: "Ghana", city: "", area: "", ref_no: "",
    property_type: "residential", investment_category: "primary_residence",
    description: "", public_summary: "", internal_notes: "",
    price: "", currency: "USD", request_pricing_only: false,
    availability_status: "draft", is_public: false, is_featured: false,
    featured_image: "", gallery: [], floor_plans: [], videos: [], documents: [],
    bedrooms: "", bathrooms: "", area_sqm: "", land_size_sqm: "",
    amenities: [], completion_date: "", commission_notes: "",
  });
  const [amenityInput, setAmenityInput] = useState("");
  const [galleryInput, setGalleryInput] = useState("");

  useEffect(() => {
    if (isNew) return;
    api.get(`/opportunities/${id}`).then((r) => setO({ ...r.data, price: r.data.price ?? "", bedrooms: r.data.bedrooms ?? "", bathrooms: r.data.bathrooms ?? "", area_sqm: r.data.area_sqm ?? "", land_size_sqm: r.data.land_size_sqm ?? "" }));
  }, [id, isNew]);

  const save = async () => {
    const payload = {
      ...o,
      price: o.price === "" ? null : parseFloat(o.price),
      bedrooms: o.bedrooms === "" ? null : parseInt(o.bedrooms),
      bathrooms: o.bathrooms === "" ? null : parseInt(o.bathrooms),
      area_sqm: o.area_sqm === "" ? null : parseFloat(o.area_sqm),
      land_size_sqm: o.land_size_sqm === "" ? null : parseFloat(o.land_size_sqm),
    };
    try {
      if (isNew) {
        const { data } = await api.post("/opportunities", payload);
        toast.success("Created");
        navigate(`/altura-command/opportunities/${data.id}`);
      } else {
        await api.patch(`/opportunities/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) { toast.error("Failed to save"); }
  };

  const addAmenity = () => { if (amenityInput.trim()) { setO({ ...o, amenities: [...(o.amenities || []), amenityInput.trim()] }); setAmenityInput(""); } };
  const rmAmenity = (i) => setO({ ...o, amenities: o.amenities.filter((_, idx) => idx !== i) });
  const addGallery = () => { if (galleryInput.trim()) { setO({ ...o, gallery: [...(o.gallery || []), galleryInput.trim()] }); setGalleryInput(""); } };
  const rmGallery = (i) => setO({ ...o, gallery: o.gallery.filter((_, idx) => idx !== i) });

  const inp = "admin-input";
  const lbl = "text-[11px] uppercase tracking-[0.2em] text-altura-charcoal/60";

  return (
    <div data-testid={`page-opp-editor-${id}`}>
      <AdminHeader breadcrumb={isNew ? "New" : "Edit"} title={isNew ? "New Opportunity" : o.title || "Opportunity"}
        subtitle="All fields are versioned; save to publish."
        actions={
          <>
            <Link to="/altura-command/opportunities" className="btn-ghost-espresso"><ArrowLeft className="h-3.5 w-3.5" /> Back</Link>
            <button onClick={save} className="btn-gold" data-testid="opp-save"><Save className="h-3.5 w-3.5" /> Save</button>
          </>
        }
      />
      <div className="p-8 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="admin-card space-y-4">
            <div className="font-editorial text-lg text-altura-espresso">Basics</div>
            <div><label className={lbl}>Title</label><input className={inp} value={o.title} onChange={(e) => setO({ ...o, title: e.target.value })} data-testid="opp-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Reference #</label><input className={inp} value={o.ref_no} onChange={(e) => setO({ ...o, ref_no: e.target.value })} data-testid="opp-ref" /></div>
              <div><label className={lbl}>Country</label>
                <select className={inp} value={o.country} onChange={(e) => setO({ ...o, country: e.target.value })} data-testid="opp-country">
                  {markets.map((m) => <option key={m.key}>{m.label.includes("(") ? m.label.split(" (")[0] : m.label}</option>)}
                </select>
              </div>
              <div><label className={lbl}>City</label><input className={inp} value={o.city} onChange={(e) => setO({ ...o, city: e.target.value })} data-testid="opp-city" /></div>
              <div><label className={lbl}>Area / Neighborhood</label><input className={inp} value={o.area} onChange={(e) => setO({ ...o, area: e.target.value })} data-testid="opp-area" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Property type</label>
                <select className={inp} value={o.property_type} onChange={(e) => setO({ ...o, property_type: e.target.value })} data-testid="opp-property-type">
                  {propertyTypes.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div><label className={lbl}>Investment category</label>
                <select className={inp} value={o.investment_category} onChange={(e) => setO({ ...o, investment_category: e.target.value })} data-testid="opp-category">
                  {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="admin-card space-y-4">
            <div className="font-editorial text-lg text-altura-espresso">Content</div>
            <div><label className={lbl}>Public summary (1–2 lines)</label><textarea rows={2} className={inp} value={o.public_summary || ""} onChange={(e) => setO({ ...o, public_summary: e.target.value })} data-testid="opp-summary" /></div>
            <div><label className={lbl}>Description (public)</label><textarea rows={6} className={inp} value={o.description || ""} onChange={(e) => setO({ ...o, description: e.target.value })} data-testid="opp-description" /></div>
            <div><label className={lbl}>Internal notes (not published)</label><textarea rows={3} className={inp} value={o.internal_notes || ""} onChange={(e) => setO({ ...o, internal_notes: e.target.value })} data-testid="opp-internal" /></div>
          </div>

          <div className="admin-card space-y-4">
            <div className="font-editorial text-lg text-altura-espresso">Media</div>
            <div><label className={lbl}>Featured image URL</label><input className={inp} value={o.featured_image || ""} onChange={(e) => setO({ ...o, featured_image: e.target.value })} data-testid="opp-featured-image" /></div>
            <div>
              <label className={lbl}>Gallery URLs</label>
              <div className="flex gap-2 mt-1">
                <input className={inp} value={galleryInput} onChange={(e) => setGalleryInput(e.target.value)} placeholder="Paste image URL and press add" data-testid="opp-gallery-input" />
                <button onClick={addGallery} className="btn-ghost-espresso whitespace-nowrap" data-testid="opp-gallery-add">Add</button>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-2">
                {(o.gallery || []).map((src, i) => (
                  <div key={i} className="relative aspect-square rounded overflow-hidden group">
                    <img src={src} alt="" className="h-full w-full object-cover" />
                    <button onClick={() => rmGallery(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-altura-espresso/80 text-altura-ivory opacity-0 group-hover:opacity-100"><X className="h-3 w-3 mx-auto" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="admin-card space-y-4">
            <div className="font-editorial text-lg text-altura-espresso">Details</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><label className={lbl}>Bedrooms</label><input type="number" className={inp} value={o.bedrooms ?? ""} onChange={(e) => setO({ ...o, bedrooms: e.target.value })} data-testid="opp-beds" /></div>
              <div><label className={lbl}>Bathrooms</label><input type="number" className={inp} value={o.bathrooms ?? ""} onChange={(e) => setO({ ...o, bathrooms: e.target.value })} data-testid="opp-baths" /></div>
              <div><label className={lbl}>Area (m²)</label><input type="number" className={inp} value={o.area_sqm ?? ""} onChange={(e) => setO({ ...o, area_sqm: e.target.value })} data-testid="opp-area-sqm" /></div>
              <div><label className={lbl}>Land size (m²)</label><input type="number" className={inp} value={o.land_size_sqm ?? ""} onChange={(e) => setO({ ...o, land_size_sqm: e.target.value })} data-testid="opp-land-sqm" /></div>
            </div>
            <div>
              <label className={lbl}>Amenities</label>
              <div className="flex gap-2 mt-1">
                <input className={inp} value={amenityInput} onChange={(e) => setAmenityInput(e.target.value)} placeholder="e.g. Private Pool" data-testid="opp-amenity-input" />
                <button onClick={addAmenity} className="btn-ghost-espresso" data-testid="opp-amenity-add">Add</button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(o.amenities || []).map((a, i) => (
                  <span key={i} className="badge-soft flex items-center gap-2">{a}<button onClick={() => rmAmenity(i)}><X className="h-3 w-3" /></button></span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="admin-card space-y-4">
            <div className="font-editorial text-lg text-altura-espresso">Publishing</div>
            <div><label className={lbl}>Status</label>
              <select className={inp} value={o.availability_status} onChange={(e) => setO({ ...o, availability_status: e.target.value })} data-testid="opp-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!o.is_public} onChange={(e) => setO({ ...o, is_public: e.target.checked })} data-testid="opp-public" /> Publish publicly</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!o.is_featured} onChange={(e) => setO({ ...o, is_featured: e.target.checked })} data-testid="opp-featured" /> Feature on Home & Market pages</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!o.request_pricing_only} onChange={(e) => setO({ ...o, request_pricing_only: e.target.checked })} data-testid="opp-request-pricing" /> Hide price · "Request Pricing"</label>
          </div>
          <div className="admin-card space-y-3">
            <div className="font-editorial text-lg text-altura-espresso">Pricing</div>
            <div><label className={lbl}>Price</label><input type="number" className={inp} value={o.price ?? ""} onChange={(e) => setO({ ...o, price: e.target.value })} data-testid="opp-price" /></div>
            <div><label className={lbl}>Currency</label><input className={inp} value={o.currency} onChange={(e) => setO({ ...o, currency: e.target.value })} data-testid="opp-currency" /></div>
          </div>
          <div className="admin-card space-y-3">
            <div className="font-editorial text-lg text-altura-espresso">Timing & internal</div>
            <div><label className={lbl}>Completion date</label><input className={inp} value={o.completion_date || ""} onChange={(e) => setO({ ...o, completion_date: e.target.value })} placeholder="Q4 2026" data-testid="opp-completion" /></div>
            <div><label className={lbl}>Commission notes (internal)</label><textarea rows={2} className={inp} value={o.commission_notes || ""} onChange={(e) => setO({ ...o, commission_notes: e.target.value })} data-testid="opp-commission" /></div>
          </div>
        </aside>
      </div>
    </div>
  );
}
